import { Button, Stack, Text } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import type { Contract, WeekReport } from '@/api/generated/models';
import { useListContracts } from '@/api/generated/contracts/contracts';
import {
  getListWeekReportsQueryKey,
  useGenerateWeekReport
} from '@/api/generated/week-reports/week-reports';
import { FormModal, ModalFooter, MutationButton, SelectField } from '@/components/common';
import { formatWeekLabel } from '@/features/week-reports/weekReportDisplay';
import { invalidateActivityQueries } from '@/utils/invalidateActivityQueries';
import { getRecentIsoWeekOptions } from '@/utils/isoWeek';

interface GenerateWeekReportModalProps {
  opened: boolean;
  onClose: () => void;
}

export const GenerateWeekReportModal = ({ opened, onClose }: GenerateWeekReportModalProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [contractId, setContractId] = useState<string | null>(null);
  const [weekValue, setWeekValue] = useState<string | null>(null);

  const { data: contractsData } = useListContracts();
  const contracts: Contract[] = contractsData ?? [];

  const weekOptions = useMemo(
    () =>
      getRecentIsoWeekOptions().map((option) => ({
        value: option.value,
        label: formatWeekLabel(option.week, option.year)
      })),
    []
  );

  const reset = () => {
    setContractId(null);
    setWeekValue(null);
  };

  const generateMutation = useGenerateWeekReport({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: (created) => {
        queryClient.invalidateQueries({ queryKey: getListWeekReportsQueryKey() });
        // Generiranje zaključava sve slobodne aktivnosti tog tjedna na novi izvještaj.
        invalidateActivityQueries(queryClient);
        const createdId = (created as WeekReport | undefined)?.id;
        reset();
        onClose();
        if (createdId) {
          navigate(`/weekly-reports/${createdId}`, {
            state: { savedMessage: t('weekReports.generate.success') }
          });
        }
      }
    }
  });

  const handleSubmit = () => {
    if (!contractId || !weekValue) return;
    const [week, year] = weekValue.split('-').map(Number);
    generateMutation.mutate({ data: { contractId, weekNumber: week, reportYear: year } });
  };

  return (
    <FormModal
      opened={opened}
      onClose={() => {
        reset();
        onClose();
      }}
      title={
        <Text fz={18} fw={700} style={{ color: 'var(--color-text-primary)' }}>
          {t('weekReports.generate.title')}
        </Text>
      }
      styles={{
        content: { backgroundColor: 'var(--color-bg-surface)' },
        header: {
          backgroundColor: 'var(--color-bg-surface)',
          borderBottom: '1px solid var(--color-border-soft)'
        },
        body: { padding: 20 }
      }}
    >
      <Stack gap={16}>
        <Text fz={13} style={{ color: 'var(--color-text-secondary)' }}>
          {t('weekReports.generate.hint')}
        </Text>

        <SelectField
          label={t('weekReports.generate.fieldContract')}
          required
          data={contracts
            .filter((c) => c.id)
            .map((c) => ({ value: c.id as string, label: c.contractNumber ?? c.name ?? '' }))}
          value={contractId}
          onChange={setContractId}
        />
        <SelectField
          label={t('weekReports.generate.fieldWeek')}
          required
          data={weekOptions}
          value={weekValue}
          onChange={setWeekValue}
        />

        {generateMutation.isError && (
          <Text fz={13} style={{ color: 'var(--color-status-danger)' }}>
            {t('weekReports.generate.error')}
          </Text>
        )}

        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            {t('common.cancel')}
          </Button>
          <MutationButton
            isPending={generateMutation.isPending}
            isPaused={generateMutation.isPaused}
            disabled={!contractId || !weekValue}
            onClick={handleSubmit}
          >
            {t('weekReports.generate.submit')}
          </MutationButton>
        </ModalFooter>
      </Stack>
    </FormModal>
  );
};
